// DashboardPage.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, Tooltip as PieTooltip 
} from 'recharts';
import styles from './ChatApp.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;;

const ProductManagementTable = ({ styles, API_BASE_URL }) => {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStock, setFilterStock] = useState('All');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        const fetchProducts = async () => {
            setIsLoading(true);
            setError('');
            try {
                const response = await axios.get(`${API_BASE_URL}/products`);
                
                setProducts(response.data || []);
            } catch (err) {
                let errorMsg = 'Failed to fetch products.';
                if (err.response && err.response.data && err.response.data.detail) {
                    errorMsg = err.response.data.detail;
                }
                setError(errorMsg);
                console.error("Error fetching products:", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, [API_BASE_URL]);

    const categories = useMemo(() => {
        const categorySet = new Set(products.map(p => p.category || 'Uncategorized'));
        return Array.from(categorySet).sort();
    }, [products]);

    const processedData = useMemo(() => {
        let filteredProducts = [...products];

        if (filterStock === 'In Stock') {
            filteredProducts = filteredProducts.filter(p => (p.stock_quantity || 0) > 0);
        } else if (filterStock === 'Out of Stock') {
            filteredProducts = filteredProducts.filter(p => (p.stock_quantity || 0) <= 0);
        }

        if (filterCategory) {
            filteredProducts = filteredProducts.filter(p => p.category === filterCategory);
        }

        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            filteredProducts = filteredProducts.filter(p =>
                (p.product_name && p.product_name.toLowerCase().includes(lowerCaseQuery)) ||
                (p.sku && p.sku.toLowerCase().includes(lowerCaseQuery))
            );
        }
        
        const totalItems = filteredProducts.length;
        
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));
        
        const startIndex = (validCurrentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = filteredProducts.slice(startIndex, endIndex);

        return { paginatedData, totalPages, totalItems, validCurrentPage };

    }, [products, searchQuery, filterCategory, filterStock, currentPage, itemsPerPage]);

    const { paginatedData, totalPages, totalItems, validCurrentPage } = processedData;

    useEffect(() => {
        if (currentPage !== validCurrentPage) {
            setCurrentPage(validCurrentPage);
        }
    }, [currentPage, validCurrentPage]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryChange = (e) => {
        setFilterCategory(e.target.value);
        setCurrentPage(1);
    };

    const handleStockChange = (e) => {
        setFilterStock(e.target.value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        setCurrentPage(p => Math.max(1, p - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(p => Math.min(totalPages, p + 1));
    };

    return (
        <div className={`${styles.adminCard} ${styles.gridColSpan3}`}>
            <h3 className={styles.adminSectionTitle}>Product Management</h3>
            
            {/* --- Search & Filter Controls --- */}
            <div className={styles.tableControls}>
                <input
                    type="text"
                    placeholder="Search by name or SKU..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={styles.adminInput}
                />
                <select 
                    value={filterCategory} 
                    onChange={handleCategoryChange} 
                    className={styles.adminSelect}
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <select 
                    value={filterStock} 
                    onChange={handleStockChange} 
                    className={styles.adminSelect}
                >
                    <option value="All">All Stock</option>
                    <option value="In Stock">In Stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                </select>
            </div>

            {/* --- Data Table --- */}
            <div className={styles.tableResponsive}>
                <table className={styles.analyticsTable}>
                    <thead>
                        <tr>
                            <th>Product Name</th>
                            <th>Description</th>
                            <th>SKU</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Stock Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="6">Loading products...</td></tr>
                        ) : error ? (
                             <tr><td colSpan="6" className={styles.adminError}>{error}</td></tr>
                        ) : paginatedData.length === 0 ? (
                            <tr><td colSpan="6">No products found matching your criteria.</td></tr>
                        ) : (
                            paginatedData.map(product => (
                                <tr key={product.id || product.sku}>
                                    <td data-label="Product">{product.productName}</td>
                                    <td data-label="Description">{product.description}</td>
                                    <td data-label="SKU">{product.sku}</td>
                                    <td data-label="Category">{product.category}</td>
                                    <td data-label="Price">${Number(product.sellingPrice || product.price || 0).toFixed(2)}</td>
                                    <td data-label="Stock">{Number(product.stock_quantity !== undefined ? product.stock_quantity : (product.stockQuantity || 0)).toLocaleString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- Pagination Controls --- */}
            {!isLoading && !error && products.length > 0 && (
                <div className={styles.paginationControls}>
                    <div className={styles.paginationInfo}>
                        <span>
                            Showing {totalItems > 0 ? (1 + (validCurrentPage - 1) * itemsPerPage) : 0}
                            - {Math.min(validCurrentPage * itemsPerPage, totalItems)} of {totalItems}
                        </span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span>per page</span>
                    </div>
                    <div className={styles.paginationButtonNav}>
                        <button 
                            onClick={handlePrevPage} 
                            disabled={validCurrentPage === 1}
                            className={styles.adminButton}
                        >
                            Previous
                        </button>
                        <span>Page {validCurrentPage} of {totalPages || 1}</span>
                        <button 
                            onClick={handleNextPage} 
                            disabled={validCurrentPage === totalPages || totalPages === 0}
                            className={styles.adminButton}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};


const DollarIcon = () => <span>$</span>;
const OrdersIcon = () => <span>📦</span>;
const ProductsIcon = () => <span>🏷️</span>;
const AOVIcon = () => <span>🧾</span>; 

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const revenue = payload.find(p => p.dataKey === 'daily_revenue');
        const orders = payload.find(p => p.dataKey === 'orders_count');
        return (
            <div className={styles.chartTooltip}>
                <p className={styles.chartTooltipLabel}>{label}</p>
                {revenue && <p className={styles.chartTooltipIntro}>
                    {`Revenue: $${revenue.value.toFixed(2)}`}
                </p>}
                 {orders && <p className={styles.chartTooltipIntro}>
                    {`Orders: ${orders.value}`}
                </p>}
            </div>
        );
    }
    return null;
};

const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className={styles.chartTooltip}>
                <p className={styles.chartTooltipLabel}>{data.category}</p>
                <p className={styles.chartTooltipIntro}>{`Revenue: $${data.total_revenue.toFixed(2)}`}</p>
                <p className={styles.chartTooltipIntro}>{`Qty Sold: ${data.total_quantity_sold.toLocaleString()}`}</p>
            </div>
        );
    }
    return null;
};


const PIE_COLORS = ['#006837', '#f2a900', '#007bff', '#28a745', '#6c757d', '#ffc107', '#17a2b8'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if ((percent * 100) < 5) {
        return null;
    }

    return (
        <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12px" fontWeight="bold">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};


const DashboardPage = () => {
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchAnalytics = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/analytics/dashboard`);
            setAnalytics(response.data);
        } catch (err) {
            let errorMsg = 'Failed to fetch analytics.';
            if (err.response && err.response.data && err.response.data.detail) {
                errorMsg = err.response.data.detail;
            }
            setError(errorMsg);
            console.error("Error fetching analytics:", err);
            setAnalytics(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const salesChartData = useMemo(() => analytics?.sales_by_date || [], [analytics]);
    const categoryChartData = useMemo(() => analytics?.sales_by_category || [], [analytics]);

    // Formatters for chart axes
    const formatCurrency = (tickItem) => `$${tickItem.toLocaleString()}`;
    const formatNumber = (tickItem) => tickItem.toLocaleString();
    const formatPercent = (value) => `${Number(value).toFixed(1)}%`;


    const renderTable = (title, data, columns, keyPrefix = 'table') => {
        if (!data || data.length === 0) {
            return (
                <div className={styles.adminCard}>
                    <h3 className={styles.adminSectionTitle}>{title}</h3>
                    <p>No data available to display.</p>
                </div>
            );
        }

        return (
             <div className={styles.adminCard}>
                <h3 className={styles.adminSectionTitle}>{title}</h3>
                <div className={styles.tableResponsive}>
                    <table className={styles.analyticsTable}>
                        <thead>
                            <tr>
                                {columns.map(col => <th key={`${keyPrefix}-th-${col.key}`}>{col.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={`${keyPrefix}-tr-${index}`}>
                                    {columns.map(col => (
                                        <td key={`${keyPrefix}-td-${index}-${col.key}`} data-label={col.label}>
                                            {col.isCurrency ? `$${Number(row[col.key] || 0).toFixed(2)}` 
                                            : col.isNumeric ? Number(row[col.key] || 0).toLocaleString()
                                            : col.isPercent ? formatPercent(Number(row[col.key] || 0))
                                            : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.adminPageContainer}>
            <div className={styles.adminHeader}>
              <h2 className={styles.adminPageTitle}>Sales & Product</h2>
              <button onClick={fetchAnalytics} disabled={isLoading} className={styles.refreshButton}>
                {isLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
            
            {isLoading && <p>Loading analytics...</p>}
            {error && <div className={styles.adminError}>{error}</div>}

            {analytics && (
                <div className={styles.kpiGrid}>
                    <div className={styles.kpiCard}>
                        <div className={`${styles.kpiIcon} ${styles.iconPrimary}`}>
                            <DollarIcon />
                        </div>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Total Sales</div>
                            <div className={styles.kpiValue}>
                                ${analytics.summary.totalSales.toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className={styles.kpiCard}>
                        <div className={`${styles.kpiIcon} ${styles.iconAccent}`}>
                            <OrdersIcon />
                        </div>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Total Orders</div>
                            <div className={styles.kpiValue}>{analytics.summary.totalOrders}</div>
                        </div>
                    </div>
                    <div className={styles.kpiCard}>
                        <div className={`${styles.kpiIcon} ${styles.iconInfo}`}>
                            <AOVIcon />
                        </div>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Average Order Value</div>
                            <div className={styles.kpiValue}>
                                ${analytics.summary.averageOrderValue.toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className={styles.kpiCard}>
                        <div className={`${styles.kpiIcon} ${styles.iconNeutral}`}>
                            <ProductsIcon />
                        </div>
                        <div className={styles.kpiInfo}>
                            <div className={styles.kpiLabel}>Products in Catalog</div>
                            <div className={styles.kpiValue}>{analytics.summary.totalProductsInCatalog}</div>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.pageGrid}>
                
                {analytics && (
                    <>
                        {/* --- Sales Chart --- */}
                        <div className={`${styles.adminCard} ${styles.gridColSpan2}`}>
                            <h3 className={styles.adminSectionTitle}>Sales & Orders Over Time (Last 30 Days)</h3>
                            <div style={{ width: '100%', height: 350 }}> 
                                <ResponsiveContainer>
                                    <LineChart 
                                        data={salesChartData}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                                        <XAxis 
                                            dataKey="date" 
                                            stroke="var(--text-light)"
                                            fontSize={12}
                                            tick={{ dy: 5 }}
                                        />
                                        <YAxis 
                                            yAxisId="left"
                                            stroke="var(--text-light)" 
                                            fontSize={12}
                                            tickFormatter={formatCurrency}
                                            label={{ value: 'Revenue', angle: -90, position: 'insideLeft', fill: 'var(--text-light)', dx: -15 }}
                                        />
                                        <YAxis 
                                            yAxisId="right" 
                                            orientation="right" 
                                            stroke="var(--text-light)" 
                                            fontSize={12}
                                            tickFormatter={formatNumber}
                                            label={{ value: 'Orders', angle: 90, position: 'insideRight', fill: 'var(--text-light)', dx: 15 }}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend verticalAlign="top" height={36}/>
                                        <Line 
                                            yAxisId="left"
                                            type="monotone" 
                                            dataKey="daily_revenue" 
                                            name="Revenue"
                                            stroke="var(--primary-color)" 
                                            strokeWidth={2.5}
                                            dot={{ r: 4, fill: "var(--primary-color)" }}
                                            activeDot={{ r: 6, stroke: "var(--bg-white)", strokeWidth: 2, fill: "var(--primary-hover)" }}
                                        />
                                         <Line 
                                            yAxisId="right"
                                            type="monotone" 
                                            dataKey="orders_count" 
                                            name="Orders"
                                            stroke="var(--accent-color)" 
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: "var(--accent-color)" }}
                                            activeDot={{ r: 5, stroke: "var(--bg-white)", strokeWidth: 1, fill: "var(--accent-hover)" }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* --- Category Pie Chart --- */}
                        <div className={styles.adminCard}>
                            <h3 className={styles.adminSectionTitle}>Revenue by Category</h3>
                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={categoryChartData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={110}
                                            fill="#8884d8"
                                            dataKey="total_revenue"
                                            nameKey="category"
                                        >
                                            {categoryChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <PieTooltip content={<CustomPieTooltip />} />
                                        <Legend 
                                            layout="vertical" 
                                            verticalAlign="middle" 
                                            align="right" 
                                            iconSize={10}
                                            wrapperStyle={{ fontSize: '13px', lineHeight: '20px' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </>
                )}
            
                {analytics && (
                    <>
                         {/* --- Sales Rep Penetration --- */}
                         {renderTable(
                            'Sales Rep Penetration', 
                            analytics.sales_rep_penetration,
                            [
                                { key: 'rep_name', label: 'Sales Rep' },
                                { key: 'stores_assigned', label: 'Assigned', isNumeric: true },
                                { key: 'stores_sold_to', label: 'Sold To', isNumeric: true },
                                { key: 'penetration_pct', label: 'Penetration', isPercent: true }
                            ],
                            'rep-pen'
                        )}

                        {/* --- Inactive Stores --- */}
                        {renderTable(
                            'Inactive Stores (Churn Risk)', 
                            analytics.inactive_stores,
                            [
                                { key: 'store_name', label: 'Store' },
                                { key: 'days_since_last_order', label: 'Days Since Last Order', isNumeric: true }
                            ],
                            'inactive'
                        )}

                        {/* --- Dead Stock --- */}
                        {renderTable(
                            'Top 10 Dead Stock (Products Never Sold)', 
                            (analytics.dead_stock_products).slice(0, 10),
                            [
                                { key: 'product_name', label: 'Product' },
                                { key: 'sku', label: 'SKU' },
                                { key: 'category', label: 'Category' },
                            ],
                            'dead-stock'
                        )}

                        {/* --- Top Products Qty --- */}
                        {renderTable(
                            'Top 5 Products (by Quantity)', 
                            analytics.top_products_by_qty,
                            [
                                { key: 'product_name', label: 'Product' },
                                { key: 'total_quantity_sold', label: 'Qty Sold', isNumeric: true }
                            ],
                            'top-prod-qty'
                        )}
                        
                        {/* --- Worst Products Qty --- */}
                        {renderTable(
                            'Worst 5 Products (by Quantity)', 
                            analytics.worst_products_by_qty,
                            [
                                { key: 'product_name', label: 'Product' },
                                { key: 'total_quantity_sold', label: 'Qty Sold', isNumeric: true }
                            ],
                            'worst-prod-qty'
                        )}

                        {/* --- Top Products Rev --- */}
                        {renderTable(
                            'Top 5 Products (by Revenue)', 
                            analytics.top_products_by_revenue,
                            [
                                { key: 'product_name', label: 'Product' },
                                { key: 'total_revenue', label: 'Revenue', isCurrency: true }
                            ],
                            'top-prod-rev'
                        )}

                        {/* --- AOV by Rep --- */}
                        {renderTable(
                            'Average Order Value (by Rep)', 
                            analytics.aov_by_rep,
                            [
                                { key: 'sales_rep_name', label: 'Sales Rep' },
                                { key: 'average_order_value', label: 'AOV', isCurrency: true },
                                { key: 'total_orders', label: 'Total Orders', isNumeric: true }
                            ],
                             'aov-rep'
                        )}

                         {/* --- AOV by Store --- */}
                         {renderTable(
                            'Average Order Value (by Store)', 
                            analytics.aov_by_store,
                            [
                                { key: 'store_name', label: 'Store' },
                                { key: 'average_order_value', label: 'AOV', isCurrency: true },
                                { key: 'total_orders', label: 'Total Orders', isNumeric: true }
                            ],
                             'aov-store'
                        )}
                    </>
                )}

                <ProductManagementTable 
                    styles={styles} 
                    API_BASE_URL={API_BASE_URL} 
                />
            </div>
        </div>
    );
};

export default DashboardPage;